// Gli abitanti di PopTown (artboard 56): ognuno decide da sé cosa fare quando è libero (cercare un
// collega e parlarci, andare in un luogo, passeggiare, giocare a palla). Nessun giro fisso: scelte
// a caso, durate variabili, incontri che dipendono da chi è libero e vicino. Come si muovono sta in
// crowd.ts.
import { PLACES, friends, pickTalk } from './cast';
import { Crowd, SPACE, rand, type Ball, type Pop } from './crowd';

export { depth, type Ball, type Pop, type Rect } from './crowd';

export class Town extends Crowd {
  balls: Ball[] = [];
  private used = new Set<string>();
  private cooldown = new Map<string, number>();
  private clock = 0;

  private free(p: Pop) {
    return !p.partner && (p.state === 'idle' || (p.state === 'walk' && p.then === null));
  }

  private decide(p: Pop) {
    const roll = Math.random();
    const mates = friends(p.role);
    const idle = this.pops.filter(
      (o) => o !== p && this.free(o) && (this.cooldown.get(this.pair(p, o)) ?? 0) < this.clock,
    );
    const friend = idle.filter((o) => mates.includes(o.role));
    const far = (o: Pop) => Math.hypot(o.x - p.x, o.y - p.y);
    const nearest = (list: Pop[]) => [...list].sort((m, n) => far(m) - far(n));
    // il più delle volte si cerca un collega, il più vicino fra quelli liberi; senza colleghi,
    // a volte due chiacchiere con chi è vicino
    if (roll < 0.75) {
      for (const other of nearest(friend).slice(0, 3)) if (this.meet(p, other)) return;
      if (roll < 0.35)
        for (const other of nearest(idle.filter((o) => far(o) < 450)).slice(0, 2))
          if (this.meet(p, other)) return;
    } else if (roll < 0.88) {
      const liked = PLACES.filter((pl) => pl.likes.includes(p.role));
      const pool = liked.length && Math.random() < 0.6 ? liked : PLACES;
      const place = pool[Math.floor(Math.random() * pool.length)];
      const at = this.snap({ x: place.x + rand(-60, 60), y: place.y + rand(-16, 16) });
      if (at && this.inView(at) && !this.crowded(at, p) && this.go(p, at, 'place')) {
        p.place = place;
        return;
      }
    }
    if (roll > 0.94 && idle.length) {
      const other = idle[Math.floor(Math.random() * idle.length)];
      if (this.play(p, other)) return;
    }
    // altrimenti due passi qui vicino: così si incontra qualcun altro
    if (!this.go(p, this.spot(p, 220, p), null)) p.timer = rand(1, 3);
  }

  private pair(a: Pop, b: Pop) {
    return [a.id, b.id].sort().join('-');
  }

  /** due pop si danno appuntamento a metà strada, uno di fronte all'altro */
  private meet(a: Pop, b: Pop): boolean {
    const mid = this.spot({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, 160);
    const left = this.snap({ x: mid.x - 44, y: mid.y });
    const right = this.snap({ x: mid.x + 44, y: mid.y });
    // vicino a un bordo i due punti possono finire uno sull'altro: l'incontro allora non parte
    if (!left || !right || Math.abs(left.y - right.y) > 30) return false;
    if (Math.hypot(left.x - right.x, left.y - right.y) < 70) return false;
    if (this.crowded(left, a, SPACE * 0.9) || this.crowded(right, b, SPACE * 0.9)) return false;
    const [pa, pb] = a.x < b.x ? [left, right] : [right, left];
    if (!this.go(a, pa, 'meet') || !this.go(b, pb, 'meet')) {
      a.state = b.state = 'idle';
      return false;
    }
    a.partner = b;
    b.partner = a;
    return true;
  }

  private play(a: Pop, b: Pop): boolean {
    const mid = this.spot(a, 200);
    const left = this.snap({ x: mid.x - 70, y: mid.y });
    const right = this.snap({ x: mid.x + 70, y: mid.y });
    if (!left || !right || Math.hypot(left.x - right.x, left.y - right.y) < 110) return false;
    if (!this.go(a, left, 'play') || !this.go(b, right, 'play')) {
      a.state = b.state = 'idle';
      return false;
    }
    a.partner = b;
    b.partner = a;
    return true;
  }

  /** le conversazioni in corso: chi ha cercato l'altro (`s0`) e chi è stato cercato (`s1`) */
  private talks: { s0: Pop; s1: Pop; lines: [0 | 1, string][]; i: number; wait: number }[] = [];

  protected arrive(p: Pop) {
    p.state = 'idle';
    if (p.then === 'place' && p.place) {
      p.state = 'stay';
      p.timer = rand(...p.place.stay);
      p.facing = Math.random() < 0.5 ? 1 : -1;
      if (Math.random() < 0.8)
        this.say(p, p.place.says[Math.floor(Math.random() * p.place.says.length)]);
      return;
    }
    const o = p.partner;
    if ((p.then === 'meet' || p.then === 'play') && o) {
      p.facing = o.x > p.x ? 1 : -1;
      if (o.state !== 'idle' || o.then !== p.then) {
        p.state = 'idle';
        p.timer = 15; // aspetta il compagno; se non arriva, l'appuntamento salta
        return;
      }
      // arrivati quasi nello stesso punto: l'appuntamento salta, invece di stare uno sull'altro
      if (Math.hypot(o.x - p.x, o.y - p.y) < 50) {
        this.cancel(p);
        return;
      }
      o.facing = p.x > o.x ? 1 : -1;
      if (p.then === 'meet') {
        const lines = pickTalk(o.role, p.role, this.used);
        p.state = o.state = 'talk';
        this.talks.push({ s0: o, s1: p, lines, i: 0, wait: 0.3 });
      } else {
        p.state = o.state = 'play';
        this.balls.push({ from: o, to: p, t: 0, left: rand(8, 14) });
      }
    }
  }

  private part(a: Pop, b: Pop) {
    for (const p of [a, b]) {
      p.state = 'idle';
      p.partner = null;
      p.then = null;
      p.timer = rand(0.5, 2.5);
    }
    this.cooldown.set(this.pair(a, b), this.clock + rand(12, 30));
  }

  step(dt: number) {
    this.clock += dt;
    for (const p of this.pops) {
      if (p.bubble && (p.bubble.left -= dt) <= 0) p.bubble = null;
      p.moving = false;
      if (p.state === 'idle' && (p.timer -= dt) <= 0) {
        if (p.partner) this.cancel(p);
        this.decide(p);
      } else if (p.state === 'stay' && (p.timer -= dt) <= 0) {
        p.state = 'idle';
        p.place = null;
        p.then = null;
        p.timer = rand(0.5, 2);
      } else if (p.state === 'walk') this.walk(p, dt);
    }
    this.converse(dt);
    this.toss(dt);
    this.separate(dt);
  }

  private converse(dt: number) {
    this.talks = this.talks.filter((talk) => {
      const { s0, s1 } = talk;
      if (s0.state !== 'talk' || s1.state !== 'talk') return false;
      if ((talk.wait -= dt) > 0) return true;
      if (talk.i >= talk.lines.length) {
        this.part(s0, s1);
        return false;
      }
      const [who, text] = talk.lines[talk.i++];
      const speaker = who === 0 ? s0 : s1;
      this.say(speaker, text);
      talk.wait = (speaker.bubble?.left ?? 2) + 0.35;
      return true;
    });
  }

  private toss(dt: number) {
    this.balls = this.balls.filter((ball) => {
      const { from, to } = ball;
      if (from.state !== 'play' || to.state !== 'play') return false;
      ball.t += dt / 1.1;
      if (ball.t >= 1) {
        ball.t = 0;
        ball.from = to;
        ball.to = from;
        if (Math.random() < 0.18)
          this.say(to, ['Presa!', 'Tocca a te!', 'Bel tiro!'][Math.floor(Math.random() * 3)]);
      }
      if ((ball.left -= dt) <= 0) {
        this.part(from, to);
        return false;
      }
      return true;
    });
  }
}
